import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import "./Profile.css";
import "./ProfileRedesign.css";
import ProfileSidebar from "./ProfileSidebar";
import ProfileContent from "./ProfileContent";
import { MyContext } from "../../../../Context/MyContext";
import MyBookings from "./MyBookings";
import KycPage from "./KycPage";
import ResetPasswordPage from "../auth/ResetPasswordPage";
import MyWishlist from "./MyWishlist";
import MyEnquiries from "./MyEnquiries";
import AppIcon from "../../../../Components/AppIcon";

const NAV_ITEMS = [
  { id: "content", label: "My Listings", icon: "home" },
  { id: "bookings", label: "Bookings", icon: "calendar" },
  { id: "enquiries", label: "My Enquiries", icon: "enquiry" },
  { id: "wishlist", label: "Wishlist", icon: "wishlist" },
  { id: "kyc", label: "KYC", icon: "kyc" },
  { id: "reset", label: "Security", icon: "security" },
];

const ProfilePage = () => {
  const { user, imgUrl } = useContext(MyContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("all");
  const [activeView, setActiveView] = useState(() => {
    if (searchParams.get('view') === 'kyc') return 'kyc';
    return location.state?.activeView || 'content';
  });
  const [kycRole, setKycRole] = useState(
    () => location.state?.kycRole || searchParams.get('role') || 'owner',
  );

  useEffect(() => {
    if (location.state?.activeView) {
      setActiveView(location.state.activeView);
      if (location.state?.kycRole) setKycRole(location.state.kycRole);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state?.activeView, location.state?.kycRole, navigate]);

  useEffect(() => {
    if (searchParams.get('view') === 'kyc') {
      setActiveView('kyc');
      if (searchParams.get('role')) setKycRole(searchParams.get('role'));
    }
  }, [searchParams]);

  const [formData, setFormData] = useState({
    name: user?.user_name || "",
    email: user?.email || "",
    phone: user?.user_phone || "",
    location: user?.location || "",
  });

  const [verified, setVerifiedState] = useState(() => {
    try {
      const saved = localStorage.getItem("profile_verified");
      return saved ? JSON.parse(saved) : { email: true, phone: true };
    } catch {
      return { email: true, phone: true };
    }
  });

  const setVerified = (updater) => {
    setVerifiedState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      try { localStorage.setItem("profile_verified", JSON.stringify(next)); } catch {}
      return next;
    });
  };

  if (!user) {
    return (
      <div className="acct-gate">
        <div className="acct-gate__card">
          <div className="acct-gate__icon" aria-hidden="true">
            <AppIcon name="lock" size={32} />
          </div>
          <h2>Sign in to your account</h2>
          <p>
            Access your listings, bookings, wishlist, and profile settings in one place.
          </p>
          <button type="button" className="acct-gate__btn" onClick={() => navigate("/login")}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const initials = (user.user_name || user.email || "U")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="acct-page">
      <header className="acct-hero">
        <div className="acct-hero__bg" aria-hidden="true" />
        <div className="acct-hero__inner">
          <div className="acct-hero__avatar">
            {user.profile ? (
              <img src={imgUrl ? imgUrl(user.profile) : user.profile} alt="" />
            ) : (
              <span>{initials}</span>
            )}
          </div>
          <div className="acct-hero__info">
            <span className="acct-hero__eyebrow">My Account</span>
            <h1 className="acct-hero__name">{user.user_name || "Member"}</h1>
            <p className="acct-hero__email">{user.email}</p>
          </div>
        </div>
      </header>

      <nav className="acct-nav" aria-label="Account sections">
        {NAV_ITEMS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`acct-nav__item ${activeView === item.id ? "acct-nav__item--active" : ""}`}
            onClick={() => setActiveView(item.id)}
          >
            <span className="acct-nav__icon" aria-hidden="true">
              <AppIcon name={item.icon} size={16} />
            </span>
            {item.label}
          </button>
        ))}
      </nav>

      <div className="acct-body">
        <aside className="acct-sidebar-wrap">
          <ProfileSidebar
            formData={formData}
            setFormData={setFormData}
            verified={verified}
            setVerified={setVerified}
            setActiveView={setActiveView}
          />
        </aside>
        <main className="acct-main">
          {activeView === "content" && (
            <ProfileContent activeTab={activeTab} setActiveTab={setActiveTab} />
          )}
          {activeView === "bookings" && (
            <MyBookings setActiveView={setActiveView} />
          )}
          {activeView === "kyc" && (
            <KycPage setActiveView={setActiveView} initialRole={kycRole} />
          )}
          {activeView === "reset" && (
            <ResetPasswordPage setActiveView={setActiveView} />
          )}
          {activeView === "wishlist" && (
            <MyWishlist setActiveView={setActiveView} />
          )}
          {activeView === "enquiries" && (
            <MyEnquiries setActiveView={setActiveView} />
          )}
        </main>
      </div>
    </div>
  );
};

export default ProfilePage;
